import { useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { api, qs } from '../lib/api';
import { fmtDate, fmtDateTime, fmtMoney } from '../lib/format';
import type { Statement } from '../lib/types';
import { Button, ErrorState, Input, Ltr } from '../components/ui';
import { IconPrinter } from '../components/icons';
import { useOpenDocument } from '../components/Layout';

/**
 * Customer statement (כרטסת לקוח). Built as a printable page: "הדפסה / PDF" opens the
 * browser's print dialog, where "שמירה כ-PDF" produces the file to send the customer.
 */
export function StatementPage() {
  const id = Number(useParams().id);
  const [params, setParams] = useSearchParams();
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  const open = useOpenDocument();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['statement', id, from, to],
    queryFn: () => api<Statement>(`/api/customers/${id}/statement${qs({ from, to })}`),
  });
  const set = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    setParams(next, { replace: true });
  };

  // The PDF's default file name comes from the page title.
  useEffect(() => {
    if (!data) return;
    const prev = document.title;
    document.title = `כרטסת ${data.customer.businessName} ${new Date().toLocaleDateString('he-IL').replace(/\./g, '-')}`;
    return () => {
      document.title = prev;
    };
  }, [data]);

  if (error) return <div className="panel"><ErrorState error={error} onRetry={() => refetch()} /></div>;

  return (
    <>
      <div className="page-head no-print">
        <div>
          <div className="crumbs">
            <Link to="/customers">לקוחות</Link> / {data ? <Link to={`/customers/${id}`}>{data.customer.businessName}</Link> : '…'}
          </div>
          <h1>כרטסת לקוח</h1>
          <div className="sub">לשליחה ללקוח: "הדפסה / PDF" ← "שמירה כ-PDF"</div>
        </div>
        <div className="row wrap">
          <label className="row small muted">
            מתאריך
            <Input type="date" value={from} onChange={(e) => set('from', e.target.value)} style={{ width: 150 }} />
          </label>
          <label className="row small muted">
            עד
            <Input type="date" value={to} onChange={(e) => set('to', e.target.value)} style={{ width: 150 }} />
          </label>
          <Button variant="primary" onClick={() => window.print()} disabled={!data}>
            <IconPrinter /> הדפסה / PDF
          </Button>
        </div>
      </div>

      <article className="statement-sheet panel" aria-busy={isLoading}>
        {!data ? (
          <div className="panel-body"><span className="skeleton" style={{ width: '60%' }} /></div>
        ) : (
          <>
            <header className="statement-head">
              <div>
                <img src="/brand/logo.png" alt={data.business.name} className="statement-logo" />
                <div className="small muted">
                  {data.business.name}
                  {data.business.companyNumber ? <> · ע.מ <Ltr>{data.business.companyNumber}</Ltr></> : null}
                  {data.business.phone ? <> · <Ltr>{data.business.phone}</Ltr></> : null}
                  {data.business.email ? <> · <bdi>{data.business.email}</bdi></> : null}
                  {data.business.address ? <> · {data.business.address}</> : null}
                </div>
              </div>
              <div className="statement-title">
                <h2>כרטסת לקוח</h2>
                <div className="small muted">הופק {fmtDateTime(data.generatedAt)}</div>
              </div>
            </header>

            <section className="statement-parties">
              <div>
                <div className="muted small">לכבוד</div>
                <div className="strong">{data.customer.businessName}</div>
                <div className="small">
                  {data.customer.companyNumber ? <>ח.פ <Ltr>{data.customer.companyNumber}</Ltr></> : null}
                  {data.customer.contactName ? <> · {data.customer.contactName}</> : null}
                  {data.customer.phone ? <> · <Ltr>{data.customer.phone}</Ltr></> : null}
                </div>
                {data.customer.branches ? <div className="small muted">כולל {data.customer.branches} סניפים / מחלקות</div> : null}
              </div>
              <div>
                <div className="muted small">תקופה</div>
                <div>{data.period.from ? fmtDate(data.period.from) : 'מתחילת הרישום'} – {data.period.to ? fmtDate(data.period.to) : 'היום'}</div>
              </div>
              <div className="statement-balance">
                <div className="muted small">יתרה לתשלום</div>
                <div className="kpi-value">{fmtMoney(data.openTotal)}</div>
                {data.overdueTotal > 0 ? <div className="small" style={{ color: 'var(--bad)' }}>מתוכה באיחור {fmtMoney(data.overdueTotal)}</div> : null}
              </div>
            </section>

            {data.openInvoices.length ? (
              <section>
                <h3 className="statement-h">חשבוניות פתוחות</h3>
                <table className="table statement-table">
                  <thead>
                    <tr>
                      <th>חשבונית</th>
                      <th>תאריך</th>
                      <th>לתשלום עד</th>
                      {data.customer.branches ? <th>מחלקה / סניף</th> : null}
                      <th className="num">סכום</th>
                      <th className="num">יתרה</th>
                      <th className="num">ימי איחור</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.openInvoices.map((i) => (
                      <tr key={i.id} className="clickable" onClick={() => open(i.id)}>
                        <td><Ltr className="tabular">{i.number}</Ltr></td>
                        <td className="tabular">{fmtDate(i.issueDate)}</td>
                        <td className="tabular">{fmtDate(i.dueDate)}</td>
                        {data.customer.branches ? <td className="small">{i.branch ?? '—'}</td> : null}
                        <td className="num">{fmtMoney(i.total)}</td>
                        <td className="num strong">{fmtMoney(i.open)}</td>
                        <td className="num" style={i.daysOverdue > 0 ? { color: 'var(--bad)' } : undefined}>{i.daysOverdue || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={data.customer.branches ? 5 : 4} className="strong">סה״כ לתשלום</td>
                      <td className="num strong">{fmtMoney(data.openTotal)}</td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </section>
            ) : (
              <p className="muted">אין חשבוניות פתוחות.</p>
            )}

            <section>
              <h3 className="statement-h">תנועות</h3>
              <table className="table statement-table">
                <thead>
                  <tr>
                    <th>תאריך</th>
                    <th>מסמך</th>
                    <th>פרטים</th>
                    {data.customer.branches ? <th>מחלקה / סניף</th> : null}
                    <th className="num">חובה</th>
                    <th className="num">זכות</th>
                    <th className="num">יתרה</th>
                  </tr>
                </thead>
                <tbody>
                  {data.period.from ? (
                    <tr>
                      <td className="tabular">{fmtDate(data.period.from)}</td>
                      <td colSpan={data.customer.branches ? 5 : 4} className="muted">יתרת פתיחה</td>
                      <td className="num">{fmtMoney(data.openingBalance)}</td>
                    </tr>
                  ) : null}
                  {data.entries.map((e, i) => (
                    <tr key={i} className={e.documentId ? 'clickable' : undefined} onClick={() => e.documentId && open(e.documentId)}>
                      <td className="tabular nowrap">{fmtDate(e.date)}</td>
                      <td className="nowrap">{e.typeLabel} {e.number ? <Ltr className="tabular">{e.number}</Ltr> : null}</td>
                      <td className="small">{e.description}</td>
                      {data.customer.branches ? <td className="small">{e.branch ?? '—'}</td> : null}
                      <td className="num">{e.debit ? fmtMoney(e.debit) : ''}</td>
                      <td className="num">{e.credit ? fmtMoney(e.credit) : ''}</td>
                      <td className="num strong">{fmtMoney(e.balance)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={data.customer.branches ? 4 : 3} className="strong">סה״כ</td>
                    <td className="num">{fmtMoney(data.totals.debit)}</td>
                    <td className="num">{fmtMoney(data.totals.credit)}</td>
                    <td className="num strong">{fmtMoney(data.totals.closingBalance)}</td>
                  </tr>
                </tfoot>
              </table>
              {(data.outsideHistory ?? 0) > 0 ? (
                <p className="small muted">תשלומים וזיכויים בסך {fmtMoney(data.outsideHistory)} שולמו עבור חשבוניות קודמות לתקופת הרישום ואינם נכללים ביתרה.</p>
              ) : null}
            </section>

            <footer className="statement-foot small muted">
              לבירורים: {data.business.name}
              {data.business.phone ? <> · <Ltr>{data.business.phone}</Ltr></> : null}
              {data.business.email ? <> · <bdi>{data.business.email}</bdi></> : null}
            </footer>
          </>
        )}
      </article>
    </>
  );
}
